import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { Can, usePermanentRealtimeDealers } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, DMSIDS } from "models";
import {
  AutoflexConfigSection,
  DeleteLocationModal,
  DmsConfigSection,
  PurgeLocationModal,
  doesLocationDmsConfigExists
} from "modules/DMSv3/Settings/components/AddEditLocationModal/components";

import type { DealerDMS, DealerLocation } from "models";
import type { AddEditLocationFormPayload } from "modules/DMSv3/Settings/components/AddEditLocationModal";

type AddEditLocationFormProps = {
  hasErrors: boolean;
  dmsDateRangeLimit: number | null;
  isPending: boolean;
  selectedLocationDealer: DealerDMS | undefined;
  selectedLocation: DealerLocation | undefined;
  onClose: () => void;
  onSubmit: (values: AddEditLocationFormPayload) => void;
};

const isSchedulerDMS = (dmsID: DMSIDS) =>
  [
    DMSIDS.KEYLOOP_JOBS,
    DMSIDS.KEYLOOP_MENUS,
    DMSIDS.KEYLOOP_MENUS_STAGING,
    DMSIDS.KEYLOOP_JOBS_STAGING,
    DMSIDS.NEXTLANE,
    DMSIDS.AUTOFLEX,
    DMSIDS.AUTOFLEX_QA_STAGING,
    DMSIDS.TEST_API
  ].includes(dmsID);

const FormHelper = createFormHelper<AddEditLocationFormPayload>();

export const AddEditLocationForm = ({
  hasErrors,
  dmsDateRangeLimit,
  isPending,
  selectedLocation,
  selectedLocationDealer,
  onClose,
  onSubmit
}: AddEditLocationFormProps) => {
  const { t } = useTranslation();

  const [purgeLocationId, setPurgeLocationId] = useState<number | null>(null);
  const [deleteLocationId, setDeleteLocationId] = useState<number | null>(null);

  const { dealers } = usePermanentRealtimeDealers();
  const { watch, handleSubmit } = FormHelper.useFormContext();
  const { fields: dealerLocationDmsFields, append, remove } = FormHelper.useFieldArray({ name: "dealer_location_dms" });
  const [dmsID, dealerLocationID] = watch(["dms_id", "dealer_location_id"]);

  const handleAddDmsLocation = () => {
    append({
      comment: "",
      maintenance_intervention: "",
      customer_not_approved: "",
      not_visible_to_customer: "",
      exclude_from_import: "",
      dms_database: "",
      dealer_id: null,
      dms_location_id: "",
      dealer_location_dms_id: null,
      dms_id: undefined
    });
  };

  const getLocationDropdownOptions = () => {
    if (selectedLocation) return [{ key: selectedLocation.id, value: selectedLocation.id, text: selectedLocation.name }];

    if (selectedLocationDealer) {
      const dealer = dealers.find(d => d.id === selectedLocationDealer.dealer_id);
      return dealer?.locations?.filter(loc => loc.dms_id === DMSIDS.UNKNOWN).map(loc => ({ key: loc.id, value: loc.id, text: loc.name })) || [];
    }

    return [];
  };

  useEffect(() => {
    if (dealerLocationDmsFields.length === 0) handleAddDmsLocation();
  }, [dealerLocationDmsFields]);

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">
            {!selectedLocation ? t("v8_add_location", "Add Location") : `${t("v8_edit_location", "Edit Location")} ${selectedLocation.name}`}
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={(!dealerLocationID || dmsDateRangeLimit !== null) && hasErrors}>
              <label>{t("v8_select_location", "Select Location")} </label>
              <FormHelper.Field.Dropdown
                name="dealer_location_id"
                options={getLocationDropdownOptions()}
                dropdownProps={{
                  fluid: true,
                  search: true,
                  selectOnBlur: false,
                  scrolling: true,
                  disabled: selectedLocation !== undefined
                }}
              />
              {dmsDateRangeLimit !== null && (
                <div className="field-error">
                  {t("v8_check_in_days_prior_must_be_lower_than_dms_date_range", "Online check-in days prior must be lower than the DMS date range ({{date_range}})", {
                    date_range: dmsDateRangeLimit
                  })}
                </div>
              )}
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_enter_dsn", "Enter DSN")} </label>
              <FormHelper.Field.Input name="dsn" inputProps={{ fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_automatic_appointment_refresh_interval", "Automatic appointment refresh interval")} </label>
              <FormHelper.Field.Input name="automatic_appointment_refresh_interval" inputProps={{ type: "number", fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_webhook_low_threshold_per_minute", "Webhook low threshold per minute")} </label>
              <FormHelper.Field.Input name="webhook_low_threshold_per_minute" inputProps={{ type: "number", fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        {dmsID && [DMSIDS.AUTOFLEX, DMSIDS.AUTOFLEX_QA_STAGING].includes(dmsID) && <AutoflexConfigSection />}

        <Grid columns="equal">
          <GridColumn>
            <div className="checkboxes-container">
              <FormHelper.Field.Checkbox name="dms_write_back" label={t("v8_dms_write_back", "DMS Write Back")} />
              <FormHelper.Field.Checkbox name="dms_warranty_pin" label={t("v8_set_warranty_pin_from_dms", "Set Warranty Pin From DMS")} />
              <FormHelper.Field.Checkbox name="is_dms_event_enabled" label={t("v8_dms_event", "DMS event")} />
              <FormHelper.Field.Checkbox
                name="keep_importing_after_work_started"
                label={t("v8_keep_importing_after_work_started", "Keep Importing After Work Started")}
              />
            </div>
          </GridColumn>
        </Grid>

        {dmsID && !isSchedulerDMS(dmsID) && !dealerLocationDmsFields.some(location => !location.dealer_location_dms_id) && (
          <Grid columns={16}>
            <GridColumn floated="right" width={16}>
              <div className="add-dms-config-container">
                <Form.Button color="green" onClick={handleAddDmsLocation}>
                  {t("v8_add_dms_location_id") || "Add DMS Location ID"}
                </Form.Button>
              </div>
            </GridColumn>
          </Grid>
        )}

        {dealerLocationDmsFields.map((_, index) => (
          <React.Fragment key={`location-dms-${index}`}>
            <div className="dms-config-section">
              <Divider fitted />
            </div>

            <Grid columns={16}>
              <GridColumn width={8}>
                <Form.Field>
                  <label>{t("v8_enter_dms_location_id", "Enter DMS Location ID")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="dms_location_id" />
                </Form.Field>
              </GridColumn>

              <GridColumn width={dealerLocationDmsFields.length > 1 ? 7 : 8}>
                <Form.Field>
                  <label>{t("v8_enter_database", "Enter Database")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="dms_database" inputProps={{ fluid: true }} />
                </Form.Field>
              </GridColumn>

              {dealerLocationDmsFields.length > 1 && (
                <GridColumn width={1}>
                  <Form.Field>
                    <div className="remove-dms-config-container">
                      <Form.Button icon color="red" onClick={() => remove(index)}>
                        <Icon name="trash" />
                      </Form.Button>
                    </div>
                  </Form.Field>
                </GridColumn>
              )}
            </Grid>

            <Grid columns="equal">
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_customer_not_approved", "Customer not approved")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="customer_not_approved" />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_maintenance_intervention", "Maintenance intervention")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="maintenance_intervention" />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_not_visible_to_customer", "Not visible to customer")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="not_visible_to_customer" />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field>
                  <label>{t("v8_exclude_from_import", "Exclude from import")} </label>
                  <FormHelper.Field.ArrayInput fieldArrayName="dealer_location_dms" index={index} fieldKey="exclude_from_import" />
                </Form.Field>
              </GridColumn>
            </Grid>

            <Grid>
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_comment", "Comment")} </label>
                  <FormHelper.Field.ArrayTextArea fieldArrayName="dealer_location_dms" index={index} fieldKey="comment" textAreaProps={{ rows: 5 }} />
                </Form.Field>
              </GridColumn>
            </Grid>

            {doesLocationDmsConfigExists(dmsID) && <DmsConfigSection dmsId={dmsID} index={index} />}
          </React.Fragment>
        ))}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {selectedLocation && (
            <div className="purge-delete-container">
              <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.DMSv3SettingLocation}>
                <Form.Button color="red" type="button" floated="left" onClick={() => setDeleteLocationId(selectedLocation.id)}>
                  {t("v8_delete") || "Delete"}
                  <Icon className="trash" />
                </Form.Button>
              </Can>

              {import.meta.env.PUBLIC_ENV === "dev" && (
                <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.DMSv3SettingLocation}>
                  <Form.Button color="orange" type="button" floated="left" onClick={() => setPurgeLocationId(selectedLocation.id)}>
                    {t("v8_purge") || "Purge"}
                  </Form.Button>
                </Can>
              )}
            </div>
          )}

          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.DMSv3SettingLocation}>
              <Form.Button type="button" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(onSubmit)}>
                {!selectedLocation ? t("v8_save", "Save") : t("v8_update", "Update")}
                <Icon className="check" />
              </Form.Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>

      <PurgeLocationModal dealer_location_id={purgeLocationId} onCancel={() => setPurgeLocationId(null)} onClose={onClose} />
      <DeleteLocationModal dealer_location_id={deleteLocationId} onCancel={() => setDeleteLocationId(null)} onClose={onClose} />
    </>
  );
};
